import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiArrowLeft, FiCalendar, FiCheck, FiUser, FiX } from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import LoadingOverlay from "../../components/LoadingOverlay";
import EmployeeTimesheetDetail from "../../components/timesheet/dashboard/EmployeeTimesheetDetail";
import { approveTimesheetSubmission, fetchEmployeeTimesheetSummary, fetchTimesheetApprovalDetail, rejectTimesheetSubmission } from "../../services/timesheetApi";
import { getEntityId, getWeekRange, normalizeDateKey } from "../../utils/timesheetUtils";

const statusClass = (status) => ({ approved: "bg-emerald-100 text-emerald-800", rejected: "bg-red-100 text-red-800", pending: "bg-amber-100 text-amber-800", unlocked: "bg-indigo-100 text-indigo-800" }[String(status || "").toLowerCase()] || "bg-gray-100 text-gray-700");
const statusLabel = (status) => ({ approved: "อนุมัติแล้ว", rejected: "ไม่อนุมัติ", pending: "รออนุมัติ", waiting: "รอดำเนินการ", unlocked: "ปลดล็อกให้แก้ไข" }[String(status || "").toLowerCase()] || status || "รอดำเนินการ");
const formatReviewDate = (value) => {
  const dateKey = normalizeDateKey(value);
  if (!dateKey) return "-";
  return new Date(`${dateKey}T12:00:00`).toLocaleDateString("th-TH", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const TimesheetApprovalDetailPage = () => {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [timesheetDetail, setTimesheetDetail] = useState(null);
  const [timesheetLoading, setTimesheetLoading] = useState(false);
  const [timesheetError, setTimesheetError] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [comment, setComment] = useState("");
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState(false);
  const [acting, setActing] = useState(false);
  const reasonRef = useRef(null);

  const loadDetail = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      setTimesheetDetail(null);
      setTimesheetError("");
      const approvalDetail = await fetchTimesheetApprovalDetail(submissionId);
      setDetail(approvalDetail);

      const submissionData = approvalDetail?.submission || approvalDetail || {};
      const submittedUser = approvalDetail?.user || submissionData.user || submissionData.employee || submissionData.submitter;
      const submittedSummary = approvalDetail?.summary || submissionData.summary;
      if (submittedSummary && Array.isArray(submittedSummary.clients)) {
        setTimesheetDetail({
          ...submittedSummary,
          user: submittedSummary.user || submittedUser,
        });
        return;
      }

      const userId = getEntityId(submittedUser?._id || submittedUser?.id || submissionData.userId || submissionData.employeeId);
      const from = normalizeDateKey(submissionData.periodStart || submittedSummary?.range?.from);
      const to = normalizeDateKey(submissionData.periodEnd || submittedSummary?.range?.to);
      if (!userId || !from || !to) return;

      try {
        setTimesheetLoading(true);
        setTimesheetDetail(await fetchEmployeeTimesheetSummary(userId, { from, to }));
      } catch (requestError) {
        console.error("Failed to load submitted timesheet entries:", requestError);
        setTimesheetError(
          requestError?.response?.status === 403
            ? "ไม่สามารถโหลดรายละเอียดเวลาทำงานของพนักงานคนนี้ได้"
            : requestError?.response?.data?.message || "ไม่สามารถโหลดรายละเอียดเวลาทำงานได้"
        );
      } finally {
        setTimesheetLoading(false);
      }
    } catch (requestError) {
      setError(requestError?.response?.status === 403 ? "คุณไม่มีสิทธิ์อนุมัติ Timesheet นี้" : requestError?.response?.data?.message || "ไม่สามารถโหลดรายการนี้ได้");
    } finally {
      setLoading(false);
    }
  }, [submissionId]);

  useEffect(() => { loadDetail(); }, [loadDetail]);

  const submission = detail?.submission || detail || {};
  const submittedSummary = detail?.summary || submission.summary || {};
  const range = getWeekRange(
    new Date(`${submission.periodStart || submittedSummary.range?.from || "2000-01-03"}T12:00:00`)
  );
  const hierarchy = {
    ...detail,
    user:
      detail?.user ||
      submission.user ||
      submission.employee ||
      submission.submitter,
    clients:
      submittedSummary.clients ||
      detail?.clients ||
      submission.clients ||
      [],
    totalHours:
      submittedSummary.totalHours ??
      detail?.totalHours ??
      submission.totalHours ??
      0,
  };
  const reviewDetail = {
    ...hierarchy,
    ...timesheetDetail,
    user: timesheetDetail?.user || hierarchy.user,
    clients: timesheetDetail?.clients || hierarchy.clients,
    totalHours: timesheetDetail?.totalHours ?? hierarchy.totalHours,
  };
  const reviewerName = reviewDetail.user?.username || reviewDetail.user?.email || "พนักงาน";
  const reviewFrom = normalizeDateKey(submission.periodStart || submittedSummary.range?.from) || range.from;
  const reviewTo = normalizeDateKey(submission.periodEnd || submittedSummary.range?.to) || range.to;
  const steps = submission.approvalSteps || detail?.approvalSteps || [];

  const act = async (action, successMessage, actionType) => {
    if (acting) return;
    try {
      setActing(true);
      await action();
      navigate("/timesheets/approvals", {
        state: {
          timesheetApprovalNotification: {
            action: actionType,
            message: successMessage,
          },
        },
      });
    }
    catch (requestError) {
      const status = requestError?.response?.status;
      const message = status === 409 ? "รายการนี้ไม่สามารถดำเนินการได้อีกแล้ว" : status === 403 ? "คุณไม่มีสิทธิ์อนุมัติ Timesheet นี้" : requestError?.response?.data?.message || "ไม่สามารถอัปเดตการอนุมัติได้";
      toast.error(message, { autoClose: 5000 });
      if (status === 409) loadDetail();
    } finally {
      setActing(false);
    }
  };

  const handleApprove = async () => {
    const result = await Swal.fire({
      icon: "question",
      title: "ยืนยันการอนุมัติ Timesheet?",
      text: "เมื่ออนุมัติแล้วจะไม่สามารถย้อนกลับการดำเนินการนี้ได้",
      showCancelButton: true,
      confirmButtonText: "ยืนยันอนุมัติ",
      cancelButtonText: "ยกเลิก",
      confirmButtonColor: "#059669",
    });

    if (result.isConfirmed) {
      await act(
        () => approveTimesheetSubmission(submissionId, comment),
        "อนุมัติ Timesheet สำเร็จ",
        "approved"
      );
    }
  };

  const handleReject = async () => {
    if (!reason.trim()) {
      setReasonError(true);
      reasonRef.current?.focus();
      return;
    }

    setReasonError(false);
    const result = await Swal.fire({
      icon: "warning",
      title: "ยืนยันการไม่อนุมัติ Timesheet?",
      text: "กรุณาตรวจสอบเหตุผลก่อนยืนยันการดำเนินการ",
      showCancelButton: true,
      confirmButtonText: "ยืนยันไม่อนุมัติ",
      cancelButtonText: "ยกเลิก",
      confirmButtonColor: "#dc2626",
    });

    if (result.isConfirmed) {
      await act(
        () => rejectTimesheetSubmission(submissionId, reason),
        "ไม่อนุมัติ Timesheet สำเร็จ",
        "rejected"
      );
    }
  };

  if (loading) return <div className="min-h-screen bg-gray-50 px-4 py-5 dark:bg-gray-900"><LoadingOverlay loading /></div>;
  if (error) return <div className="min-h-screen bg-gray-50 px-4 py-5 dark:bg-gray-900"><p className="rounded-3xl bg-white px-6 py-14 text-center text-sm text-red-600 shadow-sm dark:bg-gray-800">{error}</p></div>;

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-5 dark:bg-gray-900 sm:px-6" aria-busy={acting}>
      <LoadingOverlay loading={acting} />
      <button type="button" onClick={() => navigate("/timesheets/approvals")} className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-indigo-600 dark:text-gray-300"><FiArrowLeft /> กลับไปรายการอนุมัติ</button>

      <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">ตรวจสอบ Timesheet</h1>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">ลำดับการอนุมัติปัจจุบัน {submission.currentApprovalLevel || "-"}</p>
        {(submittedSummary.hasTotalHoursMismatch || (submittedSummary.submittedTotalHours !== undefined && Number(submittedSummary.submittedTotalHours) !== Number(submittedSummary.currentTotalHours ?? hierarchy.totalHours))) && <p className="mt-3 rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-500/10 dark:text-amber-200">คำเตือน: ชั่วโมงที่ส่งไม่ตรงกับชั่วโมงรวมปัจจุบันของ Timesheet</p>}
        <div className="mt-5 grid gap-3 md:grid-cols-2">{steps.map((step, index) => <div key={step._id || step.id || index} className="rounded-2xl border border-gray-200 p-4 dark:border-gray-700"><div className="flex items-center justify-between gap-3"><p className="font-semibold text-gray-900 dark:text-gray-100">ลำดับการอนุมัติ {step.level || index + 1}</p><span className={`rounded-full px-2 py-1 text-xs font-semibold ${statusClass(step.status)}`}>{statusLabel(step.status)}</span></div><p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{step.approverName || step.approver?.username || step.role || "ผู้อนุมัติ"}</p>{(step.comment || step.reason) && <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{step.comment || step.reason}</p>}<p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{step.actedAt ? new Date(step.actedAt).toLocaleString() : ""}</p></div>)}</div>
      </div>

      <div className="mt-6 rounded-3xl border border-indigo-200 bg-indigo-50/70 p-5 shadow-sm dark:border-indigo-900/60 dark:bg-indigo-500/10 sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-indigo-600 text-white"><FiUser size={20} /></div>
            <div><p className="text-sm text-indigo-700 dark:text-indigo-300">กำลังตรวจสอบ Timesheet ของ</p><h2 className="break-all text-xl font-semibold text-gray-900 dark:text-gray-100">{reviewerName}</h2></div>
          </div>
          <div className="flex items-center gap-3 rounded-2xl bg-white px-4 py-3 shadow-sm dark:bg-gray-800"><FiCalendar className="text-indigo-600 dark:text-indigo-300" size={20} /><div><p className="text-xs font-medium text-gray-500 dark:text-gray-400">ช่วงเวลาที่ส่งเพื่ออนุมัติ</p><p className="text-base font-semibold text-indigo-800 dark:text-indigo-200">{formatReviewDate(reviewFrom)} - {formatReviewDate(reviewTo)}</p></div></div>
        </div>
      </div>

      <div className="mt-4"><EmployeeTimesheetDetail detail={reviewDetail} days={range.days} viewMode="week" loading={timesheetLoading} errorMessage={timesheetError} onBack={() => navigate("/timesheets/approvals")} /></div>

      <div className="mt-6 rounded-3xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800"><div className="grid gap-4 lg:grid-cols-2"><label className="text-sm font-medium text-gray-700 dark:text-gray-300">หมายเหตุการอนุมัติ (ไม่บังคับ)<textarea value={comment} onChange={(event) => setComment(event.target.value)} disabled={acting} className="mt-2 min-h-24 w-full rounded-xl border border-gray-200 bg-white p-3 text-sm dark:border-gray-700 dark:bg-gray-900" /></label><label className={`text-sm font-medium ${reasonError ? "text-red-600 dark:text-red-300" : "text-gray-700 dark:text-gray-300"}`}>เหตุผลที่ไม่อนุมัติ<textarea ref={reasonRef} value={reason} onChange={(event) => { setReason(event.target.value); if (event.target.value.trim()) setReasonError(false); }} disabled={acting} aria-invalid={reasonError} className={`mt-2 min-h-24 w-full rounded-xl border bg-white p-3 text-sm dark:bg-gray-900 ${reasonError ? "animate-pulse border-red-500 ring-2 ring-red-200 dark:border-red-400 dark:ring-red-900/50" : "border-gray-200 dark:border-gray-700"}`} />{reasonError && <span className="mt-1 block animate-pulse text-xs text-red-600 dark:text-red-300">กรุณาระบุเหตุผลที่ไม่อนุมัติ</span>}</label></div><div className="mt-4 flex flex-wrap gap-3"><button type="button" disabled={acting} onClick={handleApprove} className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"><FiCheck /> {acting ? "กำลังบันทึก..." : "อนุมัติ"}</button><button type="button" disabled={acting} onClick={handleReject} className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"><FiX /> ไม่อนุมัติ</button></div></div>
    </div>
  );
};

export default TimesheetApprovalDetailPage;
